import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSkillGenQuestionsLogQuery, useSkillQuery, useSkillQuestionsQuery } from "@generated/graphql";
import useIsCreate from "hooks/useIsCreate";
import CircularLoading from "components/CircularLoading";
import { Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useContext, useMemo, useState } from "react";
import Info from "pages/Skill/view/components/Info";
import Questions from "pages/Skill/view/components/QuestionItem";
import ViewGridLayout from "layout/ViewGridLayout";
import useSetTabTitle from "hooks/useSetTabTitle";
import BackPageButton from "components/buttons/BackPageButton";
import qs from "qs";
import ROUTES from "constants/routes";
import { CompanyContext } from "context/companyContext";
import ButtonWithPermissions, { generateUnavailableText } from "components/buttons/ButtonWithPermissions";
import GenerateQuestionsModal from "components/modals/GenerateQuestionsModal";
import QuestionsGeneratingLogs from "components/QuestionsGeneratingLogs";

const SkillView = () => {
  const { isCreateAvailable, currentCompany } = useContext(CompanyContext);
  const navigate = useNavigate();

  const generateQuestionsUnavailableText = generateUnavailableText(
    isCreateAvailable,
    currentCompany,
    "генерации вопросов"
  );

  const isCreate = useIsCreate();
  const [searchParams] = useSearchParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]);
  const { knowledgeBlockId, knowledgeBlockTitle } = params as unknown as {
    knowledgeBlockId?: string;
    knowledgeBlockTitle?: string;
  };
  const { skillId } = useParams();
  const skillQuery = useSkillQuery({
    variables: {
      skillId: skillId!,
    },
    skip: !skillId,
  });

  const { data: skillGenQuestionsLogData, refetch: skillGenQuestionsLogRefetch } = useSkillGenQuestionsLogQuery({
    variables: { skillId: skillId! },
  });

  const skillQuestionsQuery = useSkillQuestionsQuery({
    variables: {
      skillId: skillId!,
    },
    skip: !skillId,
    fetchPolicy: "network-only",
  });

  const { data, loading } = skillQuery;

  const skill = data?.skill;

  const [openGenerateQuestionsModal, setOpenGenerateQuestionsModal] = useState<boolean>(false);
  const title = `Навык ${skill?.title ? ` «${skill.title}»` : ""}`;
  useSetTabTitle(title, skill?.title);

  const knowledgeBlock = skill?.knowledgeBlock || { id: knowledgeBlockId, title: knowledgeBlockTitle };

  const navigateToQuestion = () => {
    navigate({ pathname: `/${ROUTES.QUESTION}/create`, search: qs.stringify({ skillId }) });
  };

  const extendedLogs = skillGenQuestionsLogData?.skillGenQuestionsLog?.map((log) => ({
    ...log,
    skillTitle: skill?.title || "",
  }));

  if (loading) return <CircularLoading />;

  return (
    <>
      <ViewGridLayout
        containerSx={{ flexWrap: "nowrap", flexDirection: { md: "row", xs: "column-reverse" } }}
        Header={
          <Stack
            sx={{ width: "100%" }}
            alignItems='flex-start'>
            <BackPageButton
              defaultValue={{
                text: `Блок знаний «${knowledgeBlock.title}»`,
                link: `/${ROUTES.KNOWLEDGE_BLOCKS}/view/${knowledgeBlock.id}`,
              }}
            />
            <Stack
              direction='row'
              justifyContent='space-between'
              sx={{ width: "100%" }}
              spacing={2}>
              <Typography variant='h28'>{title}</Typography>
              {!isCreate && (
                <Stack
                  direction='row'
                  spacing={2}
                  alignItems='flex-start'>
                  <Button
                    variant='contained'
                    color='primary'
                    sx={{
                      backgroundColor: "white",
                      whiteSpace: "nowrap",
                      color: "text.primary",
                      "&:hover": {
                        color: "white",
                      },
                    }}
                    onClick={navigateToQuestion}>
                    Создать вопрос
                  </Button>
                  <ButtonWithPermissions
                    buttonText='Сгенерировать вопросы'
                    onClick={() => setOpenGenerateQuestionsModal(true)}
                    tooltipText={generateQuestionsUnavailableText}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        }
        LeftColumn={<Questions skillQuery={skillQuestionsQuery} />}
        RightColumn={
          <Stack spacing={1}>
            <QuestionsGeneratingLogs
              logs={extendedLogs}
              refetchLogs={skillGenQuestionsLogRefetch}
              afterRefetchLogs={skillQuestionsQuery.refetch}
            />
            <Info skill={skill} />
          </Stack>
        }
      />
      {openGenerateQuestionsModal && (
        <GenerateQuestionsModal
          open={openGenerateQuestionsModal}
          onSuccess={skillGenQuestionsLogRefetch}
          onClose={() => setOpenGenerateQuestionsModal(false)}
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          knowledgeBlock={skill?.knowledgeBlock!}
          // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
          skill={{ title: skill?.title!, id: skill?.id! }}
        />
      )}
    </>
  );
};

export default SkillView;
